import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import type { TargetDTO } from '@devrobot/shared';
import { CircleCheck, Globe, HeartPulse, LoaderCircle, Lock, Rocket, Search, SearchX, Settings2, TriangleAlert } from 'lucide-react';
import { useDeployments } from '../../api/queries';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { PageHeader } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonTable } from '../../components/Skeleton';
import { StateDimensions } from '../../components/StateDimensions';
import { StatTile } from '../../components/StatTile';
import { StatusDot } from '../../components/StatusBadge';
import { VisitButton } from '../../components/VisitButton';
import { listTransition } from '../../kit/hooks';
import { pluralize } from '../../lib/format';

const vtName = (id: string) => ({ '--vt': `deployment-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}` }) as CSSProperties;

type Filter = 'all' | 'running' | 'attention';
const FILTERS: [Filter, string][] = [
  ['all', 'All'],
  ['running', 'Running'],
  ['attention', 'Needs attention'],
];

type Sort = 'visited' | 'project' | 'environment' | 'deployed';
const SORTS: [Sort, string][] = [
  ['visited', 'Recently visited'],
  ['project', 'Project'],
  ['environment', 'Environment'],
  ['deployed', 'Last deployed'],
];

// Remembered like the theme and the sidebar: a display preference, not something to share in a link.
const SORT_KEY = 'devrobot:deployments-sort';

function storedSort(): Sort {
  try {
    const saved = localStorage.getItem(SORT_KEY);
    if (SORTS.some(([k]) => k === saved)) return saved as Sort;
  } catch {
    /* storage unavailable: the choice lasts for this visit */
  }
  return 'visited';
}

function rememberSort(sort: Sort): void {
  try {
    localStorage.setItem(SORT_KEY, sort);
  } catch {
    /* storage unavailable: the choice lasts for this visit */
  }
}

/** Endpoints that exist but are not serving traffic yet (DNS missing, certificate not issued). */
const endpointsPending = (t: TargetDTO) =>
  t.endpoints.filter((e) => e.kind === 'route' && (e.dns !== 'ok' || e.certificate !== 'valid')).length;

const needsAttention = (t: TargetDTO) => t.state.health === 'unhealthy' || t.config.pending || (t.state.process === 'running' && endpointsPending(t) > 0);

const deployedAt = (t: TargetDTO) => t.currentRelease?.createdAt ?? t.createdAt;

function compare(a: TargetDTO, b: TargetDTO, sort: Sort): number {
  const byName = () => `${a.project.name} ${a.environment}`.localeCompare(`${b.project.name} ${b.environment}`);
  switch (sort) {
    case 'visited':
      // Never-visited deployments keep their alphabetical order below the visited ones.
      if (a.lastVisitedAt && b.lastVisitedAt) return b.lastVisitedAt.localeCompare(a.lastVisitedAt);
      if (a.lastVisitedAt) return -1;
      if (b.lastVisitedAt) return 1;
      return byName();
    case 'environment':
      return a.environment.localeCompare(b.environment) || byName();
    case 'deployed':
      return deployedAt(b).localeCompare(deployedAt(a));
    default:
      return byName();
  }
}

function Flags({ t }: { t: TargetDTO }) {
  return (
    <span className="flex flex-wrap gap-1">
      {t.environmentProtected ? (
        <span className="badge badge-soft badge-warning badge-xs gap-1">
          <Lock className="size-3" aria-hidden="true" /> protected
        </span>
      ) : null}
      {t.config.pending ? (
        <span className="badge badge-soft badge-warning badge-xs gap-1">
          <TriangleAlert className="size-3" aria-hidden="true" /> config pending
        </span>
      ) : null}
      {t.activeJob ? (
        <Link to={`/jobs/${t.activeJob.id}`} className="badge badge-soft badge-info badge-xs gap-1 hover:brightness-95">
          <LoaderCircle className="size-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t.activeJob.kind} {t.activeJob.state}
        </Link>
      ) : null}
      {t.removedAt ? <span className="badge badge-ghost badge-xs">removed</span> : null}
    </span>
  );
}

function Release({ t }: { t: TargetDTO }) {
  if (!t.currentRelease) return <span className="text-xs text-base-content/55">nothing deployed</span>;
  return (
    <>
      <span className="font-mono text-xs">
        {t.currentRelease.branch}@{t.currentRelease.shortSha}
      </span>
      {t.currentRelease.commitTitle ? <div className="truncate text-xs text-base-content/60" title={t.currentRelease.commitTitle}>{t.currentRelease.commitTitle}</div> : null}
      <div className="text-xs text-base-content/55">
        <RelativeTime value={t.currentRelease.createdAt} />
      </div>
    </>
  );
}

function Summary({ items }: { items: TargetDTO[] }) {
  const running = items.filter((t) => t.state.process === 'running').length;
  const unhealthy = items.filter((t) => t.state.health === 'unhealthy').length;
  const pending = items.filter((t) => t.config.pending).length;
  const waiting = items.filter((t) => endpointsPending(t) > 0).length;
  return (
    <div className="dr-stagger mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile icon={CircleCheck} label="Running" value={running} tone="success" detail={`of ${items.length} ${pluralize(items.length, 'deployment')}`} />
      <StatTile icon={HeartPulse} label="Unhealthy" value={unhealthy} tone={unhealthy ? 'error' : 'neutral'} detail={unhealthy ? 'needs a look' : 'nothing reporting unhealthy'} />
      <StatTile icon={Settings2} label="Config pending" value={pending} tone={pending ? 'warning' : 'neutral'} detail={pending ? 'saved but not applied' : 'everything applied'} />
      <StatTile icon={Globe} label="Endpoints waiting" value={waiting} tone={waiting ? 'warning' : 'neutral'} detail={waiting ? 'DNS or certificate pending' : 'all endpoints ready'} />
    </div>
  );
}

/** Every deployment the user can see, across projects, with a way to open each one. */
export default function DeploymentsPage() {
  const q = useDeployments();
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>(storedSort);

  // The field updates at once; the list re-flows shortly after, inside a list transition.
  useEffect(() => {
    if (query === appliedQuery) return;
    const t = setTimeout(() => listTransition(() => setAppliedQuery(query)), 160);
    return () => clearTimeout(t);
  }, [query, appliedQuery]);

  const items = q.data;
  const visible = useMemo(() => {
    const needle = appliedQuery.trim().toLowerCase();
    return (items ?? [])
      .filter(
        (t) =>
          (filter === 'all' || (filter === 'running' ? t.state.process === 'running' : needsAttention(t))) &&
          (!needle ||
            `${t.project.name} ${t.project.key} ${t.environment} ${t.vps.name} ${t.stack} ${t.endpoints.map((e) => e.url).join(' ')}`.toLowerCase().includes(needle)),
      )
      .sort((a, b) => compare(a, b, sort));
  }, [items, appliedQuery, filter, sort]);

  return (
    <>
      <PageHeader title="Deployments" subtitle="Every application you can see, where it runs, and how to open it." />
      {q.isPending ? <SkeletonTable /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {items && items.length === 0 ? (
        <EmptyState
          icon={Rocket}
          title="Nothing deployed yet"
          action={
            <Link to="/projects" className="btn btn-primary btn-sm">
              Go to projects
            </Link>
          }
        >
          A deployment appears here once a project is deployed to an environment on a server. Open a project and use its Deploy tab, or run <code className="font-mono">devrobot deploy</code>.
        </EmptyState>
      ) : null}
      {items && items.length > 0 ? (
        <>
          <Summary items={items} />

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
            <label className="input w-full lg:w-72">
              <Search className="size-4 opacity-50" aria-hidden="true" />
              <input type="search" data-page-search placeholder="Search deployments" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search deployments" />
              <kbd className="kbd kbd-sm max-sm:hidden">/</kbd>
            </label>
            <div role="radiogroup" aria-label="Filter deployments" className="tabs tabs-box tabs-sm w-fit border border-hairline">
              {FILTERS.map(([k, label]) => (
                <label key={k} className={`tab ${filter === k ? 'tab-active' : ''}`}>
                  <input type="radio" name="deployment-filter" className="sr-only" checked={filter === k} onChange={() => listTransition(() => setFilter(k))} />
                  {label}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm lg:ml-auto">
              <span className="text-base-content/60">Sort</span>
              {/* Read the value now, not inside the transition: this select is controlled, so React
                  restores the old value on the DOM node before the deferred callback runs. */}
              <select
                className="select select-sm w-44"
                value={sort}
                onChange={(e) => {
                  const next = e.target.value as Sort;
                  rememberSort(next);
                  listTransition(() => setSort(next));
                }}
                aria-label="Sort deployments"
              >
                {SORTS.map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {visible.length === 0 ? (
            <EmptyState icon={SearchX} title="No matching deployments">
              Try another search or filter.
            </EmptyState>
          ) : (
            <div className="card dr-surface overflow-hidden">
              {/* Phones: stacked rows instead of a five-column table. */}
              <ul className="divide-y divide-hairline md:hidden">
                {visible.map((t) => (
                  <li key={t.id} data-vt style={vtName(t.id)} className="space-y-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={`/targets/${t.id}`} className="link link-hover font-medium">
                          {t.project.name} <span className="text-base-content/50">·</span> {t.environment}
                        </Link>
                        <div className="text-xs text-base-content/60">
                          on{' '}
                          <Link to={`/vps/${t.vps.id}`} className="link link-hover">
                            {t.vps.name}
                          </Link>
                        </div>
                      </div>
                      <VisitButton target={t} size="xs" />
                    </div>
                    <StateDimensions state={t.state} endpoints={t.endpoints} compact />
                    <Flags t={t} />
                    <div className="text-xs text-base-content/65">
                      <Release t={t} />
                    </div>
                  </li>
                ))}
              </ul>

              <div className="overflow-x-auto max-md:hidden">
                <table className="table table-sm">
                  <thead>
                    <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                      <th>Deployment</th>
                      <th className="min-w-56">State</th>
                      <th className="max-lg:hidden">Release</th>
                      <th>Server</th>
                      <th className="text-right">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((t) => (
                      <tr key={t.id} data-vt style={vtName(t.id)} className="align-top hover:bg-base-200/50">
                        <td className="min-w-48">
                          <Link to={`/targets/${t.id}`} className="link link-hover font-medium">
                            {t.project.name} <span className="text-base-content/50">·</span> {t.environment}
                          </Link>
                          <div className="font-mono text-xs text-base-content/55">{t.stack}</div>
                          <div className="mt-1">
                            <Flags t={t} />
                          </div>
                        </td>
                        <td>
                          <StateDimensions state={t.state} endpoints={t.endpoints} compact />
                        </td>
                        <td className="max-w-56 max-lg:hidden">
                          <Release t={t} />
                        </td>
                        <td className="min-w-36 text-sm">
                          <Link to={`/vps/${t.vps.id}`} className="link link-hover">
                            {t.vps.name}
                          </Link>
                          <div className="text-xs">
                            <StatusDot kind="connection" value={t.vps.connection} />
                          </div>
                        </td>
                        <td>
                          <div className="flex justify-end">
                            <VisitButton target={t} size="xs" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
